import type { Inspector } from "./inspector";
import { getItemDef } from "./sim/items";
import {
  collarAlignment,
  engageLock,
  evaluateLockOptions,
  findNearestOtherCollar,
  isDockingModule,
  isLockable,
  LOCK_RANGE_M,
  releaseLock,
} from "./sim/docking";
import { liveModuleStats } from "./sim/live-stats";
import { moduleDisableReasons } from "./sim/module-disable";
import { effectiveCells, checkModuleEnableable, moduleOperational, shipCellSize } from "./sim/ship";
import type { ModuleDef, Ship, ShipModule, SpaceItem, World } from "./sim/types";
import { getSprite } from "./sprites";
import { makePanelHeaderRef } from "./util/panel";

type Target =
  | { kind: "module"; ship: Ship; module: ShipModule }
  | { kind: "item"; item: SpaceItem };

interface DynamicRefs {
  attractBtn?: HTMLButtonElement;
  lockBtn?: HTMLButtonElement;
  disableBtn?: HTMLButtonElement;
  modeBtn?: HTMLButtonElement;
  tradeBtn?: HTMLButtonElement;
}

export class ControlsPanel {
  private root: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private spriteEl: HTMLDivElement;
  private actionsEl: HTMLDivElement;
  private statsEl: HTMLDivElement;
  private statusEl: HTMLDivElement;
  private target: Target | null = null;
  private refs: DynamicRefs = {};
  private statRefs = new Map<string, HTMLSpanElement>();
  private lastRefreshMs = 0;

  constructor(
    private inspector: Inspector,
    private getWorld: () => World,
  ) {
    this.root = document.createElement("div");
    this.root.id = "controls-panel";
    this.root.className = "panel controls-panel";
    this.root.style.display = "none";

    const { header, title } = makePanelHeaderRef(() => this.hide());
    this.titleEl = title;

    this.spriteEl = document.createElement("div");
    this.spriteEl.className = "controls-sprite";

    this.actionsEl = document.createElement("div");
    this.actionsEl.className = "controls-actions";

    this.statsEl = document.createElement("div");
    this.statsEl.className = "controls-stats";

    this.statusEl = document.createElement("div");
    this.statusEl.className = "controls-status";

    this.root.appendChild(header);
    this.root.appendChild(this.spriteEl);
    this.root.appendChild(this.actionsEl);
    this.root.appendChild(this.statsEl);
    this.root.appendChild(this.statusEl);
    document.body.appendChild(this.root);
  }

  hide(): void {
    this.root.style.display = "none";
    this.target = null;
    this.refs = {};
    this.statRefs.clear();
  }

  /** Throttled text-only refresh; full rebuild would break mousedown→mouseup. */
  refresh(): void {
    if (!this.target || this.root.style.display === "none") return;
    const now = performance.now();
    if (now - this.lastRefreshMs < 120) return;
    this.lastRefreshMs = now;
    const world = this.getWorld();
    if (this.target.kind === "module") {
      const { ship, module } = this.target;
      if (!world.ships.has(ship.id) || module.hp <= 0) {
        this.hide();
        return;
      }
      this.updateLiveStats(ship, module);
      this.updateDisableBtn(ship, module);
      this.updateDockingDynamics(ship, module);
      this.updateModuleState(world, ship, module);
    } else if (!world.items.has(this.target.item.id)) {
      this.hide();
    }
  }

  tapAtWorld(world: World, wx: number, wy: number): boolean {
    for (const ship of world.ships.values()) {
      const hit = hitTestShipModule(ship, wx, wy);
      if (hit) {
        this.target = { kind: "module", ship, module: hit.module };
        this.rebuild();
        return true;
      }
    }
    for (const item of world.items.values()) {
      if (hitTestItem(item, wx, wy)) {
        this.target = { kind: "item", item };
        this.rebuild();
        return true;
      }
    }
    this.hide();
    return false;
  }

  private rebuild(): void {
    if (!this.target) return;
    this.root.style.display = "flex";
    this.actionsEl.innerHTML = "";
    this.statsEl.innerHTML = "";
    this.statusEl.textContent = "";
    this.refs = {};
    this.statRefs.clear();

    if (this.target.kind === "item") {
      const def = getItemDef(this.target.item.defId);
      this.titleEl.textContent = def.name;
      this.setSprite(def.sprite);
      this.addAction("INFO", () => this.openInfo());
      return;
    }

    const { ship, module } = this.target;
    const def = module.def;
    this.titleEl.textContent = def.name;
    this.setSprite(def.sprite);

    this.addAction("INFO", () => this.openInfo());

    if (canDisableModuleStatic(def)) {
      const btn = this.addAction("", () => {
        if (module.hp <= 0) return;
        module.userOff = !(module.userOff ?? false);
        this.rebuild();
      });
      this.refs.disableBtn = btn;
      this.updateDisableBtn(ship, module);
    }

    if (isDockingModule(module)) this.buildDockingControls(ship, module);

    const rows = liveModuleStats(this.getWorld(), ship, module);
    for (const r of rows) this.addStatRow(r.key, r.label);

    this.refresh();
  }

  private addStatRow(key: string, label: string): void {
    const row = document.createElement("div");
    row.className = "controls-stat-row";
    const k = document.createElement("span");
    k.className = "controls-stat-k";
    k.textContent = label;
    const v = document.createElement("span");
    v.className = "controls-stat-v";
    row.appendChild(k);
    row.appendChild(v);
    this.statsEl.appendChild(row);
    this.statRefs.set(key, v);
  }

  private updateLiveStats(ship: Ship, module: ShipModule): void {
    const rows = liveModuleStats(this.getWorld(), ship, module);
    for (const r of rows) {
      const v = this.statRefs.get(r.key);
      if (v) v.textContent = r.value;
    }
  }

  private updateDisableBtn(_ship: Ship, module: ShipModule): void {
    const btn = this.refs.disableBtn;
    if (!btn) return;
    const off = module.userOff === true;
    btn.textContent = off ? "ENABLE" : "DISABLE";
    btn.classList.toggle("active", off);
  }

  private buildDockingControls(ship: Ship, module: ShipModule): void {
    const dock = module.docking;
    if (!dock) return;
    const locked = dock.peerShipId != null;

    const modeBtn = this.addAction(
      dock.mode === "board" ? "MODE: BOARD" : "MODE: TRADE",
      () => {
        if (module.docking?.peerShipId != null) {
          this.setStatus("unlock before changing mode");
          return;
        }
        if (!module.docking) return;
        module.docking.mode = module.docking.mode === "board" ? "trade" : "board";
        this.rebuild();
      },
    );
    modeBtn.classList.toggle("mode-board", dock.mode === "board");
    modeBtn.classList.toggle("mode-trade", dock.mode === "trade");
    this.refs.modeBtn = modeBtn;

    if (!locked) {
      const attractBtn = this.addAction(
        dock.attract ? "ATTRACT ON" : "ATTRACT",
        () => {
          if (!moduleOperational(ship, module)) {
            const able = checkModuleEnableable(ship, module);
            this.setStatus(able.ok ? "module disabled" : `module offline: ${able.reason}`);
            return;
          }
          if (!module.docking) return;
          module.docking.attract = !module.docking.attract;
          this.rebuild();
        },
      );
      if (dock.attract) attractBtn.classList.add("active");
      this.refs.attractBtn = attractBtn;

      const lockBtn = this.addAction("LOCK", () => this.tryLock(ship, module));
      this.refs.lockBtn = lockBtn;
    } else {
      const unlockBtn = this.addAction("UNLOCK", () => {
        releaseLock(this.getWorld(), ship, module);
        this.rebuild();
      });
      unlockBtn.classList.add("active");
      this.refs.lockBtn = unlockBtn;
      const tradeBtn = this.addAction("TRADE", () => {
        if (this.onOpenTrade) this.onOpenTrade(ship, module);
      });
      this.refs.tradeBtn = tradeBtn;
    }
  }

  onOpenTrade: ((host: Ship, hostCollar: ShipModule) => void) | null = null;

  private updateDockingDynamics(ship: Ship, module: ShipModule): void {
    const dock = module.docking;
    if (!dock || !isDockingModule(module)) return;
    const world = this.getWorld();
    const locked = dock.peerShipId != null;

    if (!locked) {
      const lockBtn = this.refs.lockBtn;
      let canLock = true;
      let status = "";
      if (!moduleOperational(ship, module)) {
        canLock = false;
        const able = checkModuleEnableable(ship, module);
        status = able.ok ? "module disabled" : `module offline: ${able.reason}`;
      } else {
        const nearest = findNearestOtherCollar(world, ship, module);
        if (!nearest) {
          canLock = false;
          status = "no collar in range";
        } else if (nearest.distance > LOCK_RANGE_M) {
          canLock = false;
          status = `${nearest.distance.toFixed(1)} m — out of lock range`;
        } else {
          const align = collarAlignment(ship, module, nearest.ship, nearest.module);
          if (!align.aligned) {
            canLock = false;
            status = `${nearest.distance.toFixed(1)} m — misaligned`;
          } else {
            const { trade, board } = evaluateLockOptions(world, ship, module, nearest.ship, nearest.module);
            const eligible = dock.mode === "trade" ? trade : board;
            canLock = eligible.ok;
            status = `${nearest.distance.toFixed(1)} m — ${eligible.reason}`;
          }
        }
      }
      if (lockBtn) {
        lockBtn.disabled = !canLock;
        lockBtn.classList.toggle("disabled", !canLock);
      }
      this.setStatus(status);
    } else {
      const peer = dock.peerShipId != null ? world.ships.get(dock.peerShipId) : null;
      this.setStatus(
        dock.mode === "board"
          ? `boarded ${peer?.name ?? "?"} — sever to release`
          : `trading with ${peer?.name ?? "?"}`,
      );
    }
  }

  private tryLock(ship: Ship, module: ShipModule): void {
    const world = this.getWorld();
    const peer = isLockable(ship, module, world);
    if (!peer) {
      this.setStatus("no collar within lock range");
      return;
    }
    const { trade, board } = evaluateLockOptions(world, ship, module, peer.ship, peer.module);
    const dock = module.docking;
    if (!dock) return;
    const allowed = dock.mode === "trade" ? trade : board;
    if (!allowed.ok) {
      this.setStatus(allowed.reason);
      return;
    }
    const hostIdx = ship.modules.indexOf(module);
    const ok = engageLock(world, ship, hostIdx, module, peer.ship, peer.moduleIdx, peer.module);
    if (!ok) {
      this.setStatus("lock failed");
      return;
    }
    this.rebuild();
  }

  private addAction(label: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.className = "control-btn";
    b.textContent = label;
    b.addEventListener("click", onClick);
    this.actionsEl.appendChild(b);
    return b;
  }

  private setSprite(spriteId: string): void {
    try {
      this.spriteEl.innerHTML = getSprite(spriteId).svg;
      this.spriteEl.style.display = "grid";
    } catch {
      this.spriteEl.style.display = "none";
    }
  }

  private setStatus(msg: string): void {
    this.statusEl.textContent = msg;
  }

  private updateModuleState(world: World, ship: Ship, module: ShipModule): void {
    const reasons = moduleDisableReasons(world, ship, module);
    if (reasons.length > 0) {
      this.setStatus(`offline — ${reasons.join(", ")}`);
    } else if (!isDockingModule(module)) {
      this.setStatus("");
    }
  }

  private openInfo(): void {
    if (!this.target) return;
    if (this.target.kind === "module") {
      this.inspector.showModule(this.target.module);
    } else {
      this.inspector.showItem(this.target.item);
    }
  }
}

function canDisableModuleStatic(def: ModuleDef): boolean {
  return def.stats.powerOutW > 0 || def.stats.powerIdleW > 0 || def.stats.powerActiveW > 0;
}

interface ShipModuleHit {
  module: ShipModule;
}

function hitTestShipModule(ship: Ship, wx: number, wy: number): ShipModuleHit | null {
  const dx = wx - ship.body.pos.x;
  const dy = wy - ship.body.pos.y;
  const c = Math.cos(-ship.body.rot);
  const s = Math.sin(-ship.body.rot);
  const localX = dx * c - dy * s + ship.com.x;
  const localY = dx * s + dy * c + ship.com.y;
  const cell = shipCellSize(ship);
  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    const def = m.def;
    const eff = effectiveCells(def, m.facing);
    const minX = m.gx * cell;
    const minY = m.gy * cell;
    const maxX = minX + eff.w * cell;
    const maxY = minY + eff.h * cell;
    if (localX >= minX && localX < maxX && localY >= minY && localY < maxY) {
      return { module: m };
    }
  }
  return null;
}

function hitTestItem(item: SpaceItem, wx: number, wy: number): boolean {
  const def = getItemDef(item.defId);
  const r = def.sizeM * 0.5;
  const dx = wx - item.pos.x;
  const dy = wy - item.pos.y;
  return dx * dx + dy * dy <= r * r;
}
